import { useState } from 'react';
import toast from 'react-hot-toast';
import { ImportResult } from '../types';
import { apiService } from '../services/api';

interface CertificationImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CertificationImportModal({ isOpen, onClose, onSuccess }: CertificationImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showInstructions, setShowInstructions] = useState(true);

  if (!isOpen) return null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const allowedTypes = [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];

    if (!allowedTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.csv') && !selectedFile.name.endsWith('.xlsx')) {
      toast.error('Por favor, selecione um arquivo CSV ou Excel válido');
      return;
    }

    setFile(selectedFile);
    setImportResult(null);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Selecione um arquivo CSV ou Excel');
      return;
    }

    setIsUploading(true);
    try {
      const result = await apiService.importCertificationCSV(file);
      setImportResult(result);

      if (result.success) {
        toast.success(`Importação concluída! ${result.successfulImports} registro(s) incluído(s) para certificação.`);
        onSuccess();
      } else {
        toast(`Importação parcial: ${result.successfulImports} sucessos, ${result.failedImports} erros.`, {
          icon: '⚠️',
          style: {
            border: '1px solid #f59e0b',
            color: '#92400e',
          },
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao importar arquivo';
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadCertificationCSVTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template-certificacao.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Template de certificação baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar template de certificação:', error);
      toast.error('Erro ao baixar template CSV de certificação');
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    setShowInstructions(true);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black bg-opacity-50 p-4 backdrop-blur-sm sm:items-center sm:p-6">
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8" role="dialog" aria-labelledby="cert-import-title">
        <div className="mb-7 text-center">
          <h2 id="cert-import-title" className="text-xl font-semibold text-slate-900 sm:text-2xl">
            Importar para Certificação
          </h2>
        </div>

        <div className="space-y-7">
          <div className="text-sm text-slate-600">
            <span>📄 Template de planilha </span>
            <button
              onClick={handleDownloadTemplate}
              className="font-semibold text-slate-700 underline underline-offset-2 hover:text-slate-900"
              style={{ color: 'var(--astra-dark-blue)' }}
            >
              Baixar
            </button>
          </div>

          <div>
            <input
              id="cert-file"
              type="file"
              accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="cert-file"
              className="block cursor-pointer rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/40 p-10 text-center transition hover:border-slate-400 hover:bg-slate-50 sm:p-14"
            >
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-slate-500 ring-1 ring-slate-200">
                <svg className="h-9 w-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M12 13V6m0 0l-3 3m3-3l3 3" />
                </svg>
              </div>
              <p className="text-base font-medium text-slate-700">Arraste o arquivo ou clique para selecionar</p>
              <p className="mt-1 text-xs text-slate-500">CSV ou Excel</p>
            </label>

            {file && (
              <p className="mt-3 text-sm text-emerald-700">Arquivo selecionado: {file.name}</p>
            )}
          </div>

          <div className="border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={() => setShowInstructions((prev) => !prev)}
              className="flex w-full items-center justify-between text-left text-base font-semibold text-slate-800"
            >
              <span>ℹ️ Instruções de importação</span>
              <span className={`transition-transform ${showInstructions ? 'rotate-180' : ''}`}>▾</span>
            </button>

            {showInstructions && (
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                <li>Formato: CSV ou Excel.</li>
                <li>Obrigatório: nome.</li>
                <li>Telefone é opcional.</li>
                <li>Opcionais: email, cpf, endereco, processo, mandado e observacoes.</li>
              </ul>
            )}
          </div>

          {importResult && (
            <div className={`rounded-xl border p-6 ${importResult.success ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}>
              <h3 className={`mb-4 text-lg font-semibold ${importResult.success ? 'text-green-900' : 'text-yellow-900'}`}>
                Resultado da Importação
              </h3>
              <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg bg-white p-3 text-center">
                  <div className="text-2xl font-bold text-gray-900">{importResult.totalRows}</div>
                  <div className="text-sm text-gray-600">Total de linhas</div>
                </div>
                <div className="rounded-lg bg-white p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{importResult.successfulImports}</div>
                  <div className="text-sm text-gray-600">Sucessos</div>
                </div>
                <div className="rounded-lg bg-white p-3 text-center">
                  <div className="text-2xl font-bold text-red-600">{importResult.failedImports}</div>
                  <div className="text-sm text-gray-600">Erros</div>
                </div>
              </div>

              {importResult.errors.length > 0 && (
                <details className="cursor-pointer">
                  <summary className="text-sm font-semibold text-red-700 hover:text-red-800">
                    Ver erros ({importResult.errors.length})
                  </summary>
                  <div className="mt-3 max-h-40 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-3">
                    {importResult.errors.map((error, index) => (
                      <div key={index} className="border-b border-red-100 py-1 text-sm text-red-600 last:border-0">
                        {error}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row">
            <button
              onClick={handleClose}
              className="flex-1 rounded-xl border border-slate-300 bg-white px-6 py-3 font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Fechar
            </button>
            <button
              onClick={handleImport}
              disabled={!file || isUploading}
              className="flex-1 rounded-xl bg-slate-900 px-6 py-3 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUploading ? 'Importando...' : 'Incluir na Certificação'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
